import { LEGAL_COMPANY as C } from './company';
import type { LegalDoc } from './types';

export const REFUND_POLICY: LegalDoc = {
  title: 'Refund & Cancellation Policy',
  summary: 'When a Service Fee can be refunded, how to ask for it, and how long it takes.',
  lastUpdated: C.lastUpdated,
  intro: [
    `This policy forms part of the Terms of Use of ${C.entity} (“FinMin”) and applies to every Service Fee paid on the Platform. Capitalised terms have the meaning given in the Terms of Use.`,
    'The Service Fee, once paid, is non-refundable except in the circumstances described below.',
  ],
  sections: [
    {
      id: 'eligible',
      heading: 'When a refund is available',
      blocks: [
        {
          ordered: true,
          list: [
            'Early cancellation. You ask for a refund within 3 days of payment and the Service has not been initiated. A Service is initiated once it is assigned to an Official Provider or any work on it, such as reviewing your documents, has begun.',
            'FinMin cannot provide the Service. FinMin is unable to provide the Service for any reason that does not depend on you.',
            'Official portal issues. The Service cannot be performed because of issues with the IT Portal or another official portal that are beyond FinMin’s reasonable control.',
            'Exceptional circumstances after initiation. If a refund situation arises after the Service has been initiated, the portion of the Service Fee for work already done, the platform fee, and any expense already incurred (including payment-processing charges) will be deducted, and the balance refunded.',
          ],
        },
        'In cases 1 to 3 the amount you paid is refunded in full. FinMin may also, at its independent discretion, cancel or decline to provide a Service, in which case it will refund the applicable amount under this policy.',
      ],
    },
    {
      id: 'not-eligible',
      heading: 'When a refund is not available',
      blocks: [
        'Unless one of the cases above applies, refunds are not available, for example:',
        {
          list: [
            'after the return or filing has been submitted on the official portal;',
            'when you change your mind after the Service has been initiated;',
            'when the Service is delayed or cannot be completed because information, documents, approvals or e-verification are pending from you;',
            'when the Service is dissolved because it was not completed within 3 months of payment for reasons attributable to you;',
            'because the tax liability, refund or saving determined under law differs from the Draft Computation; or',
            'for taxes, interest, late fees or penalties paid to any government authority.',
          ],
        },
        'Any request on a ground not listed in this policy is at FinMin’s sole discretion, and FinMin is not obliged to accept it.',
      ],
    },
    {
      id: 'how-to-request',
      heading: 'How to ask for a cancellation or refund',
      blocks: [
        {
          list: [
            'Open the Service on the Platform and choose to request closure, selecting the reason that applies. You can also write to us from your registered email address, quoting the Service reference.',
            'While your request is being reviewed the Service is placed on hold, and no further actions or payments can be made on it.',
            'We review requests within 5 working days and tell you the outcome, including the amount approved for refund and any deductions.',
          ],
        },
      ],
    },
    {
      id: 'processing',
      heading: 'How refunds are paid',
      blocks: [
        {
          list: [
            'Approved refunds are initiated within 7 working days of approval, to the original payment method. Your bank or payment provider may take a further 5 to 7 working days to credit it.',
            'Reward points you redeemed against the Service Fee are credited back to your rewards balance and are not paid out in cash.',
            'Goods and Services Tax charged on the refunded amount is refunded or adjusted in accordance with GST law, and a credit note is issued where required.',
            'If you raise a chargeback or payment dispute with your bank for an amount that is being refunded or has been refunded, FinMin may recover the duplicate amount from you.',
          ],
        },
      ],
    },
    {
      id: 'contact',
      heading: 'Questions and grievances',
      blocks: [
        `For questions about a refund, contact ${C.supportEmail}. If you are not satisfied with the outcome, you can approach our Grievance Officer as described in the Terms of Use.`,
      ],
    },
  ],
};
